import {
  createXLinearScale,
  createYScale,
  defaultDataIdAssessor,
  getStackedData,
  IMargin,
  Orientation,
  stackedBarYScale,
  xScale
} from '@opengov/data-viz';

import { IChartConfigurator, IChartDimensions, IStackedBarChartCalculations } from './types';

import { getCategoriesFromChartData, sortCategories } from './categories';
import {
  adjustScalesForMinimumBandwidth,
  assembleAllColors,
  makeCoordinateSystems,
  ticksLengthOfShowAsPercentOfTotal
} from './chartCalculations';

export type ICreateStackedBarChartCalculations = {
  config: IChartConfigurator;
  showAsPercentOfTotal: boolean;
  orientation: Orientation;
  margin: IMargin;
  maxXTickWidth: number;
  customColors?: { [key: string]: string };
  additionalScaleData?: number[];
};

export const createStackedBarChartCalculations = ({
  config,
  showAsPercentOfTotal,
  orientation = 'vertical',
  margin,
  maxXTickWidth,
  customColors = {},
  additionalScaleData
}: ICreateStackedBarChartCalculations): IStackedBarChartCalculations => {
  const { data, sortOrder, width } = config;
  const { coordinateSystem, dimensions, margin: adjustedMargin } = makeCoordinateSystems(margin, width);

  // Pull out categories from data (excluding reserved keys name and id)
  const valueCategories = data && data.length > 0 ? getCategoriesFromChartData(data).toArray() : [];

  // Generate SVG points from raw data
  const stackedData = getStackedData(data, valueCategories, sortOrder, (d, key) =>
    d.hasOwnProperty(key) ? d[key] : null
  );

  const { sortedCategoriesForColorScale, sortedCategories } = sortCategories(data, sortOrder, showAsPercentOfTotal);
  const numberOfYTicks = ticksLengthOfShowAsPercentOfTotal(showAsPercentOfTotal);

  const createYScaleWithDimensions = (dim: IChartDimensions) =>
    showAsPercentOfTotal && orientation === 'vertical'
      ? createYScale([0, 1], dim.height, numberOfYTicks)
      : stackedBarYScale(
          orientation,
          data,
          dim,
          sortedCategories,
          null,
          defaultDataIdAssessor,
          additionalScaleData ? additionalScaleData : [],
          numberOfYTicks
        );

  const createXScaleWithDimensions = (dim: IChartDimensions) =>
    showAsPercentOfTotal && orientation === 'horizontal'
      ? createXLinearScale([0, 1], dim.width)
      : xScale(
          orientation,
          data,
          dim,
          sortedCategories,
          null,
          defaultDataIdAssessor,
          additionalScaleData ? additionalScaleData : [],
          numberOfYTicks
        );

  const adjustedScales = adjustScalesForMinimumBandwidth(
    orientation,
    adjustedMargin,
    dimensions,
    coordinateSystem,
    createYScaleWithDimensions,
    createXScaleWithDimensions
  );

  const chartColors = assembleAllColors(sortedCategoriesForColorScale, customColors);

  return {
    ...adjustedScales,
    categories: sortedCategories,
    colorMap: chartColors as any,
    data,
    margin: adjustedMargin,
    stackedData,
    xTickLabelWidth: maxXTickWidth,
    yTickLabelWidth: 0,
    xTickLabelHeight: 0,
    numberOfYTicks: numberOfYTicks,
    showAsPercentOfTotal: showAsPercentOfTotal
  };
};
